import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Box, Grid, Paper, Typography, IconButton, CircularProgress } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTheme } from "@mui/material/styles";
import { getTextColor } from '../../../../utils/colorUtils';
import { ModalProvider, useModal } from '../../../../components/Modal/ModalContext';
import MainCard from "../../../../components/cards/MainCard";

import ContentPackCoursePreviewModal from '../../../content-packs/ContentPackCoursePreviewModal'; 
import { CalendarSimulationPreviewModal } from '../modals/CalendarSimulationPreview';
import ExportToPresetModal from '../modals/ExportToPresetModal';
import AddItemModal from '../modals/AddItemModal';
import EditItemModal from '../modals/EditItemModal';
import MultiItemSelectModal from '../modals/MultiItemSelectModal';

import useCalendarData from '../../hooks/useCalendarData';
import useCalendarNavigation from '../../hooks/useCalendarNavigation';

import CalendarGrid from './CalendarGrid';
import CalendarHeader from './CalendarHeader';
import PaginationControls from './PaginationControls';

import { useGetSimulationsQuery } from "../../../../store/services/simulation-service";
import { useGetCoursesQuery } from "../../../../store/services/university-service";
import { formatDate } from '../../utils/dateUtils';

/**
 * Wrapper component with ModalProvider
 */
const ContentDeliveryCalendarWithProvider = (props) => {
    return (
        <ModalProvider>
            <ContentDeliveryCalendar {...props} />
        </ModalProvider>
    );
};

/**
 * Main calendar component
 * 
 * @param {Object} props
 * @param {string} props.mode - Calendar mode ('edit', 'create', or 'preset')
 * @param {string} props.deliveryGroupId - ID of delivery group (for edit mode)
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.showNotification - Notification handler
 * @param {Object} props.presetData - Preset data (for preset mode)
 * @param {string} props.startDate - Start date (for preset mode)
 * @returns {JSX.Element}
 */
const ContentDeliveryCalendar = ({
    mode = 'edit',
    deliveryGroupId,
    onClose,
    showNotification,
    presetData,
    startDate
}) => {
    const theme = useTheme();
    const { activeModal, setActiveModal } = useModal();

    // Preview state
    const [coursePreviewOpen, setCoursePreviewOpen] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [selectedSimId, setSelectedSimId] = useState(null);
    const [prevActiveModal, setPrevActiveModal] = useState(null);
    const [exportPresetOpen, setExportPresetOpen] = useState(false);

    // Define content type colors
    const contentTypeColors = useMemo(() => ({
        'Simulation': theme.palette.primary.main,
        'Course': theme.palette.warning.main,
        'Simulation-hover': theme.palette.primary.dark,
        'Course-hover': theme.palette.warning.dark
    }), [theme.palette.primary.main, theme.palette.warning.main, theme.palette.primary.dark, theme.palette.warning.dark]);

    // Use custom hooks
    const calendarData = useCalendarData(mode, deliveryGroupId, presetData, startDate, showNotification);
    const navigation = useCalendarNavigation(calendarData.monthsToDisplay);

    // RTK Query hooks for data
    const { data: simData } = useGetSimulationsQuery({
        perPage: 500,
        page: 0,
        includes: "",
        query: "",
        additionalQueryParams: ""
    });

    const { data: courseData } = useGetCoursesQuery({
        perPage: 500,
        page: 0,
        includes: "",
        query: "",
        additionalQueryParams: ""
    });

    // Modal management
    const handleOpenMultiItemSelect = useCallback((isOpen) => {
        calendarData.setMultiItemSelectState(prev => ({ ...prev, isOpen }));
    }, [calendarData]);

    const handleAddNewItemFromMultiSelect = useCallback(() => {
        const { date } = calendarData.multiItemSelectState;
        calendarData.setMultiItemSelectState(prev => ({ ...prev, isOpen: false }));

        calendarData.modalState.isAddModalOpen = true;
        calendarData.modalState.selectedDate = date;

        calendarData.formState.contentId = '';
        calendarData.formState.contentType = 'Simulation';
        calendarData.formState.scheduledFor = formatDate(date);
        calendarData.formState.selectedContentName = '';
    }, [calendarData]);

    // Preview function
    const handlePreview = useCallback(() => {
        if (!calendarData.formState.contentId) return;

        // Save current modal state
        if (activeModal && !['course', 'simulation'].includes(activeModal)) {
            setPrevActiveModal(activeModal);
        } else if (calendarData.modalState.isAddModalOpen) {
            setPrevActiveModal('add');
        } else if (calendarData.modalState.isEditModalOpen) {
            setPrevActiveModal('edit');
        }

        if (calendarData.formState.contentType === 'Course') {
            // Find the course ID based on the course name
            const course = courseData?.find(course => course.courseName === calendarData.formState.contentId);
            if (course) {
                setSelectedCourseId(course.id);
                setTimeout(() => {
                    setCoursePreviewOpen(true);
                    setActiveModal('course');
                }, 50);
            }
        } else if (calendarData.formState.contentType === 'Simulation') {
            // Find the simulation ID based on the simulation name
            const simulation = simData?.find(sim => sim.simulationName === calendarData.formState.contentId);
            if (simulation) {
                setSelectedSimId(simulation.id);
                setTimeout(() => {
                    setActiveModal('simulation');
                }, 50);
            }
        }
    }, [calendarData.formState.contentId, calendarData.formState.contentType, courseData, simData, setActiveModal, activeModal, calendarData.modalState.isAddModalOpen, calendarData.modalState.isEditModalOpen]);

    const handleClosePreview = useCallback(() => {
        setCoursePreviewOpen(false);
        setSelectedCourseId(null);
        setSelectedSimId(null);

        // Restore previous modal state
        if (prevActiveModal) {
            setActiveModal(prevActiveModal);
            setPrevActiveModal(null);
        } else {
            setActiveModal(null);
        }
    }, [setActiveModal, prevActiveModal]);

    const handleExportToPresetOpen = useCallback(() => {
        if (calendarData.calendarItems.length === 0) {
            showNotification('Cannot create a preset with no items', 'error');
            return;
        }
        setExportPresetOpen(true);
    }, [calendarData.calendarItems, showNotification]);

    const handleSaveChanges = useCallback(async () => {
        const success = await calendarData.handleSaveChanges();
        if (success) {
            onClose(true);
        }
    }, [calendarData, onClose]);

    if (calendarData.isLoadingRequest && mode === 'edit') {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4 }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                    Loading calendar data...
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <MainCard
                title={
                    <CalendarHeader
                        mode={mode}
                        isMoveMode={calendarData.modalState.isMoveMode}
                        deliveryGroupName={calendarData.deliveryGroupName}
                        setDeliveryGroupName={calendarData.setDeliveryGroupName}
                        unsavedChanges={calendarData.unsavedChanges}
                        isUpdating={calendarData.isUpdating}
                        isCreating={calendarData.isCreating}
                        isAdmin={calendarData.isAdmin}
                        calendarItemsCount={calendarData.calendarItems.length}
                        attemptedSave={calendarData.attemptedSave}
                        onSave={handleSaveChanges}
                        onExportToPreset={handleExportToPresetOpen}
                        onCancelMove={calendarData.handleCancelMoveMode}
                        onClose={onClose}
                    />
                }
                content={true}
                sx={{ bgcolor: 'background.default' }}
            >
                {/* Banner for preset mode */}
                {mode === 'preset' && calendarData.showBanner && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            mb: 3,
                            bgcolor: theme.palette.primary.main,
                            color: getTextColor(theme.palette.primary.main),
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderRadius: '8px'
                        }}
                    >
                        <Box>
                            <Typography variant="h6">
                                Template Schedule Starting {new Date(startDate).toLocaleDateString()}
                            </Typography>
                            <Typography variant="body2">
                                Items from the template "{presetData?.name}" have been placed on your calendar.
                                You can add, remove, or move items before creating the final schedule.
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            sx={{ color: 'inherit' }}
                            onClick={() => calendarData.setShowBanner(false)}
                        >
                            <CancelIcon />
                        </IconButton>
                    </Paper>
                )}

                {/* Banner for create mode */}
                {mode === 'create' && calendarData.calendarItems.length === 0 && calendarData.showBanner && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            mb: 3,
                            bgcolor: theme.palette.primary.main,
                            color: getTextColor(theme.palette.primary.main),
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderRadius: '8px'
                        }}
                    >
                        <Box>
                            <Typography variant="h6">
                                Click on any day to add content
                            </Typography>
                            <Typography variant="body2">
                                To create a delivery group, add at least one content item to the calendar
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            sx={{ color: 'inherit' }}
                            onClick={() => calendarData.setShowBanner(false)}
                        >
                            <CancelIcon />
                        </IconButton>
                    </Paper>
                )}

                {/* Calendar Grid */}
                <Grid container>
                    <CalendarGrid
                        monthsByYear={navigation.monthsByYear}
                        itemsByDate={calendarData.itemsByDate}
                        onDayClick={calendarData.handleDayClick}
                        isMoveMode={calendarData.modalState.isMoveMode}
                        contentTypeColors={contentTypeColors}
                    />
                </Grid>

                {/* Pagination Controls */}
                <PaginationControls
                    currentPage={navigation.currentPage}
                    totalPages={navigation.totalPages}
                    handlePreviousPage={navigation.handlePreviousPage}
                    handleNextPage={navigation.handleNextPage}
                    isMoveMode={calendarData.modalState.isMoveMode}
                />

                {/* Modals */}
                <MultiItemSelectModal
                    open={calendarData.multiItemSelectState.isOpen}
                    onClose={() => handleOpenMultiItemSelect(false)}
                    onItemClick={calendarData.handleItemClick}
                    onAddNewItem={handleAddNewItemFromMultiSelect}
                    multiItemSelectState={calendarData.multiItemSelectState}
                    contentTypeColors={contentTypeColors}
                    activeModal={activeModal}
                />

                <AddItemModal
                    open={calendarData.modalState.isAddModalOpen}
                    onClose={calendarData.handleCloseModal}
                    onAdd={calendarData.handleAddItem}
                    onPreview={handlePreview}
                    formState={calendarData.formState}
                    handleFormChange={calendarData.handleFormChange}
                    handleContentTypeChange={calendarData.handleContentTypeChange}
                    handleContentSelection={calendarData.handleContentSelection}
                    simulationData={simData}
                    courseData={courseData}
                    activeModal={activeModal}
                />

                <EditItemModal
                    open={calendarData.modalState.isEditModalOpen}
                    onClose={calendarData.handleCloseModal}
                    onUpdate={calendarData.handleUpdateItem}
                    onDelete={calendarData.handleDeleteItem}
                    onEnterMoveMode={calendarData.handleEnterMoveMode}
                    onPreview={handlePreview}
                    formState={calendarData.formState}
                    handleFormChange={calendarData.handleFormChange}
                    handleContentTypeChange={calendarData.handleContentTypeChange}
                    handleContentSelection={calendarData.handleContentSelection}
                    simulationData={simData}
                    courseData={courseData}
                    activeModal={activeModal}
                />

                <ContentPackCoursePreviewModal
                    courseId={selectedCourseId || ''}
                    open={activeModal === 'course' && !!selectedCourseId}
                    onClose={handleClosePreview}
                    isStandalone={true}
                />

                <CalendarSimulationPreviewModal
                    simulationId={selectedSimId || ''}
                    open={activeModal === 'simulation' && !!selectedSimId}
                    onClose={handleClosePreview}
                    isStandalone={true}
                />

                <ExportToPresetModal
                    open={exportPresetOpen}
                    onClose={() => setExportPresetOpen(false)}
                    calendarItems={calendarData.calendarItems}
                    showNotification={showNotification}
                />
            </MainCard>
        </Box>
    );
};

export default ContentDeliveryCalendarWithProvider;